// API service for the React E2E demo
// Determine backend URL dynamically.
// 1. If a REACT_APP_BACKEND_URL env variable is provided at build time, use it.
// 2. Otherwise, default to the origin where the frontend is served (works when Flask serves the React build).
//    This avoids hard-coding the port and prevents mismatches if the backend picks a different port at runtime.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Get available embedding models
export const getEmbeddingModels = async () => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/embeddings/models`);
    
    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching embedding models:', error);
    throw error;
  }
};

// Component search API (ImageBind only)
export const searchComponents = async (query, maxResults = 20, includeUnverified = false, useFaiss = true) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/components/search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        query, 
        max_results: maxResults,
        include_unverified: includeUnverified,
        use_faiss: useFaiss
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error searching components:', error);
    throw error;
  }
};

// Canvas similarity search API (ImageBind only)
export const searchSimilarImages = async (canvasImage, maxResults = 20) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/canvas/search-similar`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        canvas_image: canvasImage,
        max_results: maxResults
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error searching similar images:', error);
    throw error;
  }
};

// Inpaint API – generates new background/content using selected models
export const inpaintImage = async (canvasImage, components, prompt, maskPoints = [], samMasks = [], selectedModels = {}, brushMask = null, maskBlur = 0) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/inpaint`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        canvas_image: canvasImage,
        components,
        prompt,
        input_points: maskPoints,
        sam_masks: samMasks,
        selected_models: selectedModels,
        brush_mask: brushMask,
        mask_blur: maskBlur
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error ${response.status}: ${errorText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error performing inpaint:', error);
    throw error;
  }
};

// Load embeddings into cache – no params needed (imagebind only)
export const loadEmbeddings = async () => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/embeddings/load`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ embedding_type: 'imagebind', include_unverified: false }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error loading embeddings:', error);
    throw error;
  }
};

// Diagnostic/debugging API
export const checkComponents = async () => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/check-components`);
    
    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error checking components:', error);
    throw error;
  }
};

// Generate SAM masks for a given canvas image
export const generateSamMasks = async (canvasImage) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/sam/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ canvas_image: canvasImage })
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error ${response.status}: ${errorText}`);
    }
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error generating SAM masks:', error);
    throw error;
  }
};

// Generate a point-prompt SAM mask based on user-selected points
export const generatePointMask = async (canvasImage, maskPoints = []) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/sam/point`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ canvas_image: canvasImage, input_points: maskPoints })
    });
    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error generating point mask:', error);
    throw error;
  }
};

// Combine multiple masks into one
export const combineMasks = async (masks, canvasWidth = 1080, canvasHeight = 768) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/mask/combine`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        masks: masks,
        canvas_width: canvasWidth,
        canvas_height: canvasHeight
      })
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error ${response.status}: ${errorText}`);
    }
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error combining masks:', error);
    throw error;
  }
};

// Invert a mask or combination of masks
export const invertMask = async (masks, canvasWidth = 1080, canvasHeight = 768) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/mask/invert`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        masks: masks,
        canvas_width: canvasWidth,
        canvas_height: canvasHeight
      })
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error ${response.status}: ${errorText}`);
    }
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error inverting mask:', error);
    throw error;
  }
};

// Generate video from image using Kling v2.1
export const generateVideo = async (imageUrl, prompt, negativePrompt = '', mode = 'standard', duration = 5) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/video/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        start_image: imageUrl,
        prompt,
        negative_prompt: negativePrompt,
        mode,
        duration
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error ${response.status}: ${errorText}`);
    }
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error generating video:', error);
    throw error;
  }
};

// Generate components using diffusion model
export const generateComponents = async (prompt, numImages = 9) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/components/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        prompt,
        num_images: numImages
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error ${response.status}: ${errorText}`);
    }
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error generating components:', error);
    throw error;
  }
};

// Remove background from generated component
export const removeBackground = async (imageData) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/components/remove-background`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        image: imageData
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error ${response.status}: ${errorText}`);
    }
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error removing background:', error);
    throw error;
  }
};

// Upscale an image via backend (Topaz Labs model)
export const upscaleImage = async (image, options = {}) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/components/upscale`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image, ...options })
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error ${response.status}: ${errorText}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error upscaling image:', error);
    throw error;
  }
};


// Personal LoRA pipeline
export const personalInit = async ({ asset_slug, class_prompt, image }) => {
  const res = await fetch(`${BACKEND_URL}/api/personal/init`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ asset_slug, class_prompt, image })
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return await res.json();
};

export const personalAugment = async (asset_slug) => {
  const res = await fetch(`${BACKEND_URL}/api/personal/augment`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ asset_slug })
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return await res.json();
};

export const personalTrain = async ({ asset_slug, class_prompt, max_steps = 800, resolution = 1024 }) => {
  const res = await fetch(`${BACKEND_URL}/api/personal/train`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ asset_slug, class_prompt, max_steps, resolution })
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return await res.json();
};

export const personalStatus = async (asset_slug) => {
  const res = await fetch(`${BACKEND_URL}/api/personal/status?asset=${encodeURIComponent(asset_slug)}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return await res.json();
};

export const personalInfer = async ({ asset_slug, prompt }) => {
  const res = await fetch(`${BACKEND_URL}/api/personal/infer`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ asset_slug, prompt })
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return await res.json();
};

// Community API
export const communityUpload = async ({ file, brushMask, ownerUid }) => {
  const fd = new FormData();
  fd.append('file', file);
  if (brushMask) fd.append('brush_mask', brushMask);
  if (ownerUid) fd.append('owner_uid', ownerUid);
  const res = await fetch(`${BACKEND_URL}/api/community/upload`, { method:'POST', body: fd });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return await res.json();
};

export const communityVote = async (uuid, value) => {
  const res = await fetch(`${BACKEND_URL}/api/community/vote`, {
    method:'POST', headers:{'Content-Type':'application/json'},
    body: JSON.stringify({ uuid, value })
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return await res.json();
};

export const communityList = async ({ sort='hot', cursor=null, limit=20 }) => {
  const u = new URL(`${BACKEND_URL}/api/community/list`);
  u.searchParams.set('sort', sort);
  if (cursor) u.searchParams.set('cursor', String(cursor));
  u.searchParams.set('limit', String(limit));
  const res = await fetch(u.toString());
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return await res.json();
};

// Text embedding APIs
export const generateTextEmbedding = async (text, modelType = 'imagebind') => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/embeddings/text`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        text, 
        model_type: modelType 
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error generating text embedding:', error);
    throw error;
  }
};

export const saveLabelEmbedding = async (text, embedding, modelType = 'imagebind') => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/embeddings/save-label`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        text, 
        embedding, 
        model_type: modelType 
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error saving label embedding:', error);
    throw error;
  }
};

export const loadLabelEmbedding = async (text, modelType = 'imagebind') => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/embeddings/load-label`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        text, 
        model_type: modelType 
      }),
    });

    const result = await response.json();
    if (!response.ok && response.status !== 404) {
      throw new Error(`HTTP error ${response.status}`);
    }

    return result;
  } catch (error) {
    console.error('Error loading label embedding:', error);
    throw error;
  }
};

export const predictMaskedNode = async (contextNodes, embeddingType = 'imagebind', k = 5) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/masked-node/predict`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        context_nodes: contextNodes,
        embedding_type: embeddingType,
        k: k
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error ${response.status}: ${errorText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error predicting masked node:', error);
    throw error;
  }
};


const api = {
  getEmbeddingModels,
  searchComponents,
  searchSimilarImages,
  inpaintImage,
  loadEmbeddings,
  checkComponents,
  generateSamMasks,
  generatePointMask,
  combineMasks,
  invertMask,
  generateVideo,
  generateComponents,
  removeBackground,
  upscaleImage,
};

export default api;
